import { useState } from 'react';
import {
  channelsPinSet,
  channelsTest,
  meConnections,
  meConnectionsConsent,
  meLinearConnect,
  meLinearDisconnect,
  userPhoneStart,
  userPhoneVerify,
  type ApiError,
  type MyConnections,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { ErrLine, I, LoadError, pre, sx, useApi, useGuarded, usePage } from './kit';
import { passkeyStepUp } from './stepUp';
import { SmsConsentBox } from './SmsConsentBox';
import { ExtraPhones } from './ExtraPhones';
import { FamilyAlertChannels } from './FamilyAlertChannels';

/** The Aithora team: the only roles the server gives extra phones (v3/settings/extra-phones.ts). */
const TEAM = new Set(['SUPER_ADMIN', 'OPERATOR', 'SALES', 'DEVELOPER']);

// My connections (Spec V4 16 F; design T10244-10336, values L37078-37134). How Atlas reaches
// a person outside the console: their phone, a test text, the consent on file, their text PIN
// and, for the Aithora team, their own Linear. Everything here is the signed-in person's own.

const chip = (c: string, b: string) => 'display:inline-flex;align-items:center;height:20px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.06em;white-space:nowrap;color:' + c + ';background:' + b + ';';
const GOOD = chip('#12503C', 'rgba(31,107,82,.14)');
const NEUTRAL = chip('#54606C', 'rgba(34,39,46,.08)');
const RED = chip('#8A1C16', 'rgba(179,38,30,.1)');
const ROW = 'display:flex;align-items:flex-start;gap:12px;flex-wrap:wrap;row-gap:8px;padding:12px 2px;border-top:1px solid rgba(34,39,46,.1);';
const MAIN = 'flex:1 1 240px;min-width:min(100%,240px);';
const BTN = 'display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;border:1px solid rgba(34,39,46,.2);color:#22272E;';
const BTN_DARK = 'display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#264C7E;color:#fff;';
const BTN_QUIET = 'display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#54606C;font-weight:600;';
const INPUT = 'height:32px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;font-size:16px;font-family:inherit;background:#FCFAF4;box-sizing:border-box;';
const NOTE = 'font-size:16px;color:#54606C;margin-top:4px;line-height:1.5;text-wrap:pretty;';
const HEAD = 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;';
const TITLE = 'font-size:16px;font-weight:700;color:#22272E;';
const hm = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const day = (iso: string) => new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

export function MyConnectionsSection() {
  const { client, session } = useSession();
  const { lens } = usePage();
  const { wr, canWrite } = useGuarded();
  const q = useApi((c, s) => meConnections(c, { signal: s }), []);
  const [verifying, setVerifying] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeMsg, setCodeMsg] = useState('');
  // The text and call consent box beside the code: unchecked by default, recorded with the
  // code once the number is proved to be theirs (AF-1566).
  const [agree, setAgree] = useState(false);
  const [test, setTest] = useState<{ msg: string; at: number } | null>(null);
  const [pinEd, setPinEd] = useState(false);
  const [pin1, setPin1] = useState('');
  const [pin2, setPin2] = useState('');
  const [pinErr, setPinErr] = useState('');
  const [linEd, setLinEd] = useState(false);
  const [linKey, setLinKey] = useState('');
  const [err, setErr] = useState<ApiError | null>(null);

  if (!q.data) return q.error ? <LoadError error={q.error} onRetry={q.reload} /> : <Skeleton rows={5} label="Loading your connections" />;
  const d: MyConnections = q.data;
  const family = lens.lens === 'Family';
  const team = TEAM.has(String(session?.user.role ?? ''));
  const first = (d.name ?? session?.user.name ?? 'You').split(/\s+/)[0];
  const ph = d.phone.state;
  const staffSet = d.phone.staffSet;
  const pin = d.pin?.state ?? null;
  const consentOn = d.consent.onFile && ph !== 'optout';
  const steps: [string, boolean][] = [['Verify phone', ph === 'verified'], ...(d.pin ? ([['Set PIN', pin === 'set']] as [string, boolean][]) : []), ['Send a test text', !!test]];
  const setData = (next: Partial<MyConnections>) => q.setData((p) => (p ? { ...p, ...next } : p));
  const fail = (e: unknown) => setErr(e as ApiError);

  const startVerify = async () => {
    setErr(null);
    try {
      // A new number is typed; an unverified number on file gets its code again (empty body).
      const body: { phone?: string } = ph === 'verified' || ph === 'none' ? { phone: newPhone.replace(/[\s()-]/g, '') } : {};
      if ((ph === 'verified' || ph === 'none') && !body.phone) {
        setVerifying(true);
        setCodeMsg('');
        return;
      }
      const r = await userPhoneStart(client, { body });
      setVerifying(true);
      setCodeMsg(r.live ? 'Code sent. It expires in 10 minutes.' : r.reason ?? 'The code could not be sent.');
    } catch (e) {
      fail(e);
    }
  };
  const confirmCode = async () => {
    if (code.length !== 6) {
      setCodeMsg('Enter all 6 digits.');
      return;
    }
    setErr(null);
    try {
      await userPhoneVerify(client, { params: { id: session?.user.id ?? 'me' }, body: { code, consent: agree, source: 'console' } });
      setVerifying(false);
      setCode('');
      setAgree(false);
      q.reload();
    } catch (e) {
      fail(e);
    }
  };
  const agreeNow = async () => {
    setErr(null);
    try {
      const next = await meConnectionsConsent(client, { body: { granted: true, source: 'console' } });
      q.setData(() => next);
    } catch (e) {
      fail(e);
    }
  };
  const sendTest = async () => {
    if (ph !== 'verified') return;
    setErr(null);
    try {
      const r = await channelsTest(client, { body: {} });
      const when = 'Checked ' + hm(new Date()) + '.';
      setTest({
        at: Date.now(),
        msg: r.sent
          ? 'Sent ' + hm(new Date(r.at ?? Date.now())) + '. Look for it on the number above.'
          : r.notLive
            ? (family ? 'Texting is not switched on yet, so nothing was sent. Your texts start as soon as it is. ' : 'Texting is not switched on yet. Aithora’s number is still being registered with carriers, so nothing was sent. ') + when
            : 'Not sent: ' + (r.reason ?? 'the text failed') + '. ' + when,
      });
    } catch (e) {
      const m = (e as ApiError).message;
      setTest({ at: Date.now(), msg: m });
    }
  };
  const savePin = async () => {
    const a = pin1;
    if (a.length < 4) return setPinErr('Use 4 to 6 digits.');
    if (a !== pin2) return setPinErr('The two PINs do not match.');
    if (/^(\d)\1+$/.test(a) || '0123456789'.indexOf(a) >= 0 || '9876543210'.indexOf(a) >= 0) return setPinErr('Too easy to guess. Avoid repeats and runs like 1234.');
    setPinErr('');
    try {
      const stepUpToken = await passkeyStepUp(client);
      const r = await channelsPinSet(client, { body: { pin: a, stepUpToken } });
      setData({ pin: { state: r.locked ? 'locked' : r.set ? 'set' : 'unset' } });
      setPinEd(false);
      setPin1('');
      setPin2('');
    } catch (e) {
      setPinErr((e as Error).message || 'The PIN was not saved.');
    }
  };
  const saveLinear = async () => {
    const k = linKey.trim();
    if (k.length < 8) return;
    setErr(null);
    try {
      await meLinearConnect(client, { body: { key: k } });
      setLinEd(false);
      setLinKey('');
      q.reload();
    } catch (e) {
      fail(e);
    }
  };
  const disconnect = async () => {
    setErr(null);
    try {
      await meLinearDisconnect(client);
      setData({ linear: { connected: false, last4: null } });
    } catch (e) {
      fail(e);
    }
  };

  const phoneNote = ph === 'optout'
    ? 'You replied STOP, so Aithora cannot text you. Text START to the Aithora number to turn texts back on. We never turn them back on for you.'
    : staffSet
      ? 'Your Facility Admin sets staff numbers. Ask them to change it.'
      : ph === 'verified'
        ? 'Verified by a code sent to this number.'
        : ph === 'none'
          ? 'No number yet. Add one and we send a 6-digit code to prove it is yours.'
          : 'Not verified yet. We send a 6-digit code to prove the number is yours.';
  const consentNote = consentOn
    ? 'Agreed ' + (d.consent.recordedAt ? day(d.consent.recordedAt) : '') + (d.consent.source === 'verbal' ? ' by phone, recorded by ' + (d.consent.recordedByName ?? 'your admin') : ' in the console') + '. Stored with the exact words shown, so it can be proven later.'
    : 'With no opt-in, Aithora only reaches you by push and in-app alerts. A critical call skips this number and records why.';
  const lin = d.linear;

  return (
    <div data-screen-label="My connections" data-testid="settings-my-connections" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:4px;')}>
        <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;flex:0 0 7px;')} />
        <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;')}>My connections</span>
        <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
        <span style={sx('font-size:16px;color:#54606C;')}>{I(family ? 'Yours only, nobody else uses it' : first + ' · your own, nobody else uses them')}</span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;line-height:1.55;max-width:92ch;text-wrap:pretty;')}>
        {I(family
          ? 'The phone Aithora texts you on about your family member. You can reply to those texts, and text STOP at any time.'
          : 'How Atlas reaches you outside the console. You can text Atlas, approve low-risk requests with your PIN, and Pause AI by text from this number.')}
      </div>
      {steps.some((x) => !x[1]) && (
        <div style={sx('display:flex;align-items:center;gap:16px;flex-wrap:wrap;row-gap:8px;background:rgba(47,92,151,.07);border-radius:10px;padding:10px 14px;margin-top:12px;')}>
          <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I(steps.length + (family ? ' steps to get texts from Aithora:' : ' steps to use Atlas by text:'))}</span>
          {steps.map(([label, ok], i) => (
            <span key={label} style={sx('display:inline-flex;align-items:center;gap:7px;font-size:16px;color:#3C454F;')}>
              <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;font-size:16px;font-weight:800;' + (ok ? 'background:#1F6B52;color:#fff;' : 'border:1.5px solid rgba(34,39,46,.3);color:#54606C;'))}>{I(ok ? '✓' : String(i + 1))}</span>
              {I(label)}
            </span>
          ))}
        </div>
      )}
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,400px),1fr));gap:0 30px;align-items:start;margin-top:10px;')}>
        <div style={sx('min-width:0;')}>
          <div style={sx(ROW)}>
            <div style={sx(MAIN)}>
              <div style={sx(HEAD)}>
                <span style={sx(TITLE)}>Phone</span>
                <span style={sx('font-size:16px;color:#22272E;font-variant-numeric:tabular-nums;')}>{I(d.phone.masked ?? '')}</span>
                <span style={sx(ph === 'verified' ? GOOD : ph === 'optout' ? RED : NEUTRAL)}>{ph === 'verified' ? 'VERIFIED' : ph === 'optout' ? 'OPTED OUT' : 'NOT VERIFIED'}</span>
              </div>
              <div style={sx(NOTE)}>{I(phoneNote)}</div>
              {verifying && (
                <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:9px;')}>
                  {(ph === 'verified' || ph === 'none') && !codeMsg ? (
                    <>
                      <input type="tel" aria-label="New phone number" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="+1 512 555 0100" style={sx(INPUT + 'width:170px;')} />
                      <a href="#" {...wr(() => void startVerify())} style={sx(BTN_DARK)}>Send code</a>
                      <a href="#" onClick={(e) => { pre(e); setVerifying(false); }} style={sx(BTN_QUIET)}>Cancel</a>
                    </>
                  ) : (
                    <>
                      <input type="text" inputMode="numeric" aria-label="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit code" style={sx(INPUT + 'width:130px;')} />
                      <a href="#" {...wr(() => void confirmCode())} style={sx(BTN_DARK)}>Verify</a>
                      <span style={sx('font-size:16px;color:#54606C;')}>{I(codeMsg)}</span>
                      <SmsConsentBox role={d.role} checked={agree} onChange={setAgree} />
                    </>
                  )}
                </div>
              )}
            </div>
            {!staffSet && ph !== 'optout' && !verifying && (
              <a href="#" {...wr(() => void startVerify())} style={sx(BTN)}>
                {ph === 'verified' ? 'Change number' : 'Verify number'}
              </a>
            )}
          </div>
          {team && <ExtraPhones />}
          <div style={sx(ROW)}>
            <div style={sx(MAIN)}>
              <div style={sx(TITLE)}>Test text</div>
              <div style={sx(NOTE)}>{I(family ? 'Sends one text to the number above so you know Aithora can reach you.' : 'Sends one text to the number above so you know Atlas can reach you.')}</div>
              {test && <div role="status" style={sx('font-size:16px;color:#3C454F;margin-top:7px;line-height:1.5;background:rgba(47,92,151,.08);border-radius:8px;padding:7px 10px;text-wrap:pretty;')}>{I(test.msg)}</div>}
            </div>
            <a href="#" {...wr(() => void sendTest())} aria-disabled={ph !== 'verified' || !canWrite || undefined} style={sx('display:inline-flex;align-items:center;height:32px;padding:0 13px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;' + (ph === 'verified' ? 'border:1px solid rgba(34,39,46,.2);color:#22272E;' : 'background:rgba(34,39,46,.06);color:#54606C;cursor:default;'))}>
              Send me a test text
            </a>
          </div>
          <div style={sx(ROW)}>
            <div style={sx(MAIN)}>
              <div style={sx(HEAD)}>
                <span style={sx(TITLE)}>Text and call consent</span>
                <span style={sx(consentOn ? GOOD : RED)}>{consentOn ? 'ON FILE' : 'NONE ON FILE'}</span>
              </div>
              {consentOn && (
                <div style={sx('font-size:16px;color:#3C454F;margin-top:6px;line-height:1.5;background:rgba(34,39,46,.04);border-radius:8px;padding:8px 10px;text-wrap:pretty;')}>
                  {'“'}
                  {I(d.consent.wording ?? d.consentWording)}
                  {'”'}
                </div>
              )}
              <div style={sx('font-size:16px;color:#54606C;margin-top:5px;line-height:1.5;text-wrap:pretty;')}>{I(consentNote)}</div>
              {!consentOn && ph === 'verified' && !verifying && <SmsConsentBox role={d.role} checked={false} disabled={!canWrite} onChange={(on) => { if (on) void agreeNow(); }} />}
            </div>
          </div>
        </div>
        <div style={sx('min-width:0;')}>
          {d.pin && (
            <div style={sx(ROW)}>
              <div style={sx(MAIN)}>
                <div style={sx(HEAD)}>
                  <span style={sx(TITLE)}>Text PIN</span>
                  <span style={sx(pin === 'set' ? GOOD : pin === 'locked' ? RED : NEUTRAL)}>{pin === 'set' ? 'SET' : pin === 'locked' ? 'LOCKED' : 'NOT SET'}</span>
                </div>
                <div style={sx(NOTE)}>
                  {I(pin === 'locked'
                    ? 'Your PIN is locked after 5 wrong tries. Set a new one.'
                    : 'Your PIN approves low-risk requests by text, like Linear tickets. Care and emergency approvals need Face ID in the app. A PIN is only ever set here, never by text or call.')}
                </div>
                {pinEd && (
                  <>
                    <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:9px;')}>
                      <input type="password" inputMode="numeric" aria-label="New PIN" value={pin1} onChange={(e) => setPin1(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="New PIN, 4 to 6 digits" style={sx(INPUT + 'width:170px;')} />
                      <input type="password" inputMode="numeric" aria-label="Type the PIN again" value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Type it again" style={sx(INPUT + 'width:130px;')} />
                      <a href="#" {...wr(() => void savePin())} style={sx(BTN_DARK)}>Confirm with Face ID or passkey</a>
                      <a href="#" onClick={(e) => { pre(e); setPinEd(false); setPinErr(''); }} style={sx(BTN_QUIET)}>Cancel</a>
                    </div>
                    {pinErr && <div role="status" style={sx('font-size:16px;color:#8A1C16;margin-top:6px;')}>{I(pinErr)}</div>}
                  </>
                )}
              </div>
              {!pinEd && (
                <a href="#" {...wr(() => { setPinEd(true); setPin1(''); setPin2(''); setPinErr(''); })} style={sx(BTN)}>
                  {pin === 'set' ? 'Change PIN' : 'Set a new PIN'}
                </a>
              )}
            </div>
          )}
          {lin && (
            <>
              <div style={sx(ROW)}>
                <div style={sx(MAIN)}>
                  <div style={sx(HEAD)}>
                    <span style={sx(TITLE)}>Linear</span>
                    <span style={sx(lin.connected ? GOOD : NEUTRAL)}>{lin.connected ? 'CONNECTED' : 'NOT CONNECTED'}</span>
                  </div>
                  <div style={sx(NOTE)}>
                    {I(lin.connected
                      ? 'Connected with your own key' + (lin.last4 ? ', ending ' + lin.last4 : '') + '. The key is encrypted and never shown again. It is deleted with your account.'
                      : 'Lets Atlas find, create and update your Linear tickets when you ask by text. Uses your own key, checked with Linear before it is saved.')}
                  </div>
                  {linEd && (
                    <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-top:9px;')}>
                      <input type="password" aria-label="Your Linear API key" value={linKey} onChange={(e) => setLinKey(e.target.value)} placeholder="lin_api_..." style={sx(INPUT + 'flex:1 1 180px;min-width:min(100%,180px);')} />
                      <a href="#" {...wr(() => void saveLinear())} style={sx(BTN_DARK)}>Check and save</a>
                      <a href="#" onClick={(e) => { pre(e); setLinEd(false); setLinKey(''); }} style={sx(BTN_QUIET)}>Cancel</a>
                    </div>
                  )}
                </div>
                {!linEd && (
                  <a href="#" {...wr(() => (lin.connected ? void disconnect() : setLinEd(true)))} style={sx(BTN)}>
                    {lin.connected ? 'Disconnect' : 'Connect'}
                  </a>
                )}
              </div>
              <div style={sx(ROW)}>
                <div style={sx(MAIN)}>
                  <div style={sx(HEAD)}>
                    <span style={sx(TITLE)}>Gmail, Calendar, Drive</span>
                    <span style={sx(NEUTRAL.replace('background:rgba(34,39,46,.08)', 'background:rgba(34,39,46,.07)'))}>NOT BUILT YET</span>
                  </div>
                  <div style={sx(NOTE)}>So Atlas can read your mail and book time when you ask by text. Your CRM mail sync already connects Gmail and will be reused here.</div>
                </div>
              </div>
            </>
          )}
          <div style={sx('font-size:16px;color:#54606C;line-height:1.5;padding:12px 2px;border-top:1px solid rgba(34,39,46,.1);text-wrap:pretty;')}>
            Your connections are only ever used for your own requests. Every change here is on the Truth Ledger.
          </div>
        </div>
      </div>
      {family && <FamilyAlertChannels />}
      <ErrLine error={err} style="margin-top:6px;" />
    </div>
  );
}
